import { type GQL } from "@binale-tech/shared";
import { type CalendarEvent } from "./types";

// SP3 of the calendar feature (FIBU-22): overlays per-item state (done /
// acknowledged / note / color, fetched once per company by
// CalendarItemStateProvider) onto the merged CalendarEvent[] pipeline -
// works identically for BOTH `source: "user"` and `source: "derived"` items,
// keyed by the SP1 `stateKey` contract (design spec §III.5–§III.7/§14.2).
//
// `refType` is typed `string` here (not the narrower `CalendarEventSource`
// union) because the GraphQL-codegen'd `GQL.ICalendarItemStateRefType` is a
// TypeScript string enum - its members are assignable TO `string` but not to
// an unrelated string-literal union without a cast, so a plain `string`
// parameter is what actually lets both `CalendarEvent.source` (SP1's plain
// union) and `GQL.ICalendarItemState.refType` (the enum) be passed here
// without a cast at either call site.
export const stateMapKey = (refType: string, refId: string): string => `${refType}:${refId}`;

export const overlayItemState = (
    events: CalendarEvent[],
    stateByKey: ReadonlyMap<string, GQL.ICalendarItemState>,
): CalendarEvent[] =>
    events.map((e) => {
        const s = stateByKey.get(stateMapKey(e.source, e.stateKey));
        if (!s) {
            return e;
        }
        return {
            ...e,
            done: !!s.doneAt,
            acknowledged: !!s.acknowledgedAt,
            note: s.note ?? undefined,
            // A state row's color only WINS when it's actually set - a state row created
            // for an unrelated reason (e.g. just ticking `done`) has `color: null`, and
            // falling back to `undefined` there would wipe the event's own SP1 color
            // (user-set at creation, or a derived event's default) even though the user
            // never touched color at all.
            color: (s.color as GQL.IDocumentColors | null) ?? e.color,
        };
    });
